import { manifestApi } from "#/api/manifestApi";
import { showSuccessNotification } from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { HawbDeleteImpact, HawbRow } from "#/types/manifest";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { logger } from "#/utils/logger";
import { Button, Loader, Modal } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface DeleteHawbModalProps {
  opened: boolean;
  onClose: () => void;
  hawb: HawbRow;
  onDeleted: () => void;
}

const IMPACT_KEYS: (keyof HawbDeleteImpact)[] = [
  "items",
  "tags_freed",
  "scan_records",
  "tickets",
  "shipment_logs",
];

/** Delete confirmation that states exactly what goes with the consignment. */
const DeleteHawbModal = ({ opened, onClose, hawb, onDeleted }: DeleteHawbModalProps) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();
  const label = formatHawbLabel(hawb);

  const { data: impact, isPending, isError } = useQuery({
    queryKey: ["hawb-delete-impact", hawb.id],
    queryFn: async () => (await manifestApi.getDeleteImpact(hawb.id)).data,
    enabled: opened,
    staleTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: () => manifestApi.delete(hawb.id),
    onSuccess: () => {
      showSuccessNotification({ message: t("manifests.delete.success", { label }) });
      onClose();
      onDeleted();
    },
    onError: (error) => logger.error("delete consignment error:", error),
  });

  const lines = impact ? IMPACT_KEYS.filter((key) => impact[key] > 0) : [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="lg"
      title={t("manifests.delete.title")}
      closeOnClickOutside={false}
      classNames={{ title: "font-bold" }}
    >
      <div className="flex flex-col gap-4 text-sm">
        <span>{t("manifests.delete.intro", { label })}</span>
        {isPending ? (
          <Loader size="sm" />
        ) : isError || !impact ? (
          <span className="text-red-700">{t("manifests.delete.impactError")}</span>
        ) : (
          <ul className="list-disc pl-5 flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            {lines.length === 0 ? (
              <li>{t("manifests.delete.impact.nothing")}</li>
            ) : (
              lines.map((key) => <li key={key}>{t(`manifests.delete.impact.${key}`, { count: impact[key] })}</li>)
            )}
          </ul>
        )}
        <span className="font-bold">{t("manifests.delete.confirmation")}</span>
        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
          <Button size="sm" color="black" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canDelete()}>
            <Button
              color="red"
              size="sm"
              leftSection={<IconTrash size={18} />}
              loading={deleteMutation.isPending}
              disabled={!canDelete() || !impact}
              onClick={() => deleteMutation.mutate()}
            >
              {t("manifests.delete.confirmButton")}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteHawbModal;
